import { useState, useEffect } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import {
  Table, Button, Space, Tag, Popconfirm, Typography, Alert,
  Modal, Form, Input, Select, DatePicker, InputNumber, Checkbox, Row, Col,
  Tooltip, Statistic, Card,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  BellOutlined, ThunderboltOutlined, WarningOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '@/contexts/PageTitleContext'
import {
  useMaintenanceSchedules,
  useMaintenanceStats,
  useCreateMaintenanceSchedule,
  useUpdateMaintenanceSchedule,
  useDeleteMaintenanceSchedule,
  useCompleteMaintenanceSchedule,
  useSendMaintenanceReminder,
  useCreateWorkOrderFromMaintenance,
  type MaintenanceSchedule,
  type MaintenanceStatus,
} from '@/hooks/useMaintenanceSchedules'
import { useVehicles } from '@/hooks/useVehicles'
import { usePermission } from '@/hooks/usePermission'

const { Title } = Typography
const { Option } = Select

const statoTag = (s: MaintenanceSchedule) => {
  if (s.stato === 'completata') return <Tag color="green">Completata</Tag>
  if (s.stato === 'annullata') return <Tag color="default">Annullata</Tag>
  if (s.stato_scadenza === 'scaduta') return <Tag color="red" icon={<WarningOutlined />}>Scaduta</Tag>
  if (s.stato_scadenza === 'prossima') return <Tag color="orange">In scadenza</Tag>
  return <Tag color="blue">Attiva</Tag>
}

const MaintenanceSchedulesPage = () => {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const canCreate = usePermission('maintenance.create')
  const canDelete = usePermission('maintenance.delete')

  const [pageSize, setPageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | undefined>(undefined)
  const [overdueOnly, setOverdueOnly] = useState(false)

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<MaintenanceSchedule | null>(null)
  const [completeModalOpen, setCompleteModalOpen] = useState(false)
  const [completingId, setCompletingId] = useState<number | null>(null)
  const [reminderModalOpen, setReminderModalOpen] = useState(false)
  const [remindingId, setRemindingId] = useState<number | null>(null)

  const [scheduleForm] = Form.useForm()
  const [completeForm] = Form.useForm()
  const [reminderForm] = Form.useForm()

  useEffect(() => { setTitle('Manutenzioni Programmate') }, [setTitle])

  const { data: schedules = [], isLoading } = useMaintenanceSchedules({
    status_filter: statusFilter,
    overdue_only: overdueOnly,
  })
  const { data: stats } = useMaintenanceStats()
  const { data: vehiclesData } = useVehicles(1, 10000)

  const createMutation = useCreateMaintenanceSchedule()
  const updateMutation = useUpdateMaintenanceSchedule()
  const deleteMutation = useDeleteMaintenanceSchedule()
  const completeMutation = useCompleteMaintenanceSchedule()
  const reminderMutation = useSendMaintenanceReminder()
  const createWOMutation = useCreateWorkOrderFromMaintenance()

  const openCreate = () => {
    setEditingSchedule(null)
    scheduleForm.resetFields()
    setScheduleModalOpen(true)
  }

  const openEdit = (s: MaintenanceSchedule) => {
    setEditingSchedule(s)
    scheduleForm.setFieldsValue({
      ...s,
      data_scadenza: s.data_scadenza ? dayjs(s.data_scadenza) : null,
    })
    setScheduleModalOpen(true)
  }

  const handleScheduleSave = async () => {
    const values = await scheduleForm.validateFields()
    const payload = {
      ...values,
      data_scadenza: values.data_scadenza ? values.data_scadenza.format('YYYY-MM-DD') : undefined,
    }
    if (editingSchedule) {
      await updateMutation.mutateAsync({ id: editingSchedule.id, data: payload })
    } else {
      await createMutation.mutateAsync(payload)
    }
    setScheduleModalOpen(false)
  }

  const openComplete = (id: number) => {
    setCompletingId(id)
    completeForm.resetFields()
    setCompleteModalOpen(true)
  }

  const handleComplete = async () => {
    if (!completingId) return
    const values = completeForm.getFieldsValue()
    await completeMutation.mutateAsync({
      id: completingId,
      data: {
        data_completamento: values.data_completamento ? values.data_completamento.format('YYYY-MM-DD') : undefined,
        km_completamento: values.km_completamento,
        note_completamento: values.note_completamento,
      },
    })
    setCompleteModalOpen(false)
    setCompletingId(null)
  }

  const openReminder = (id: number) => {
    setRemindingId(id)
    reminderForm.resetFields()
    setReminderModalOpen(true)
  }

  const handleReminder = async () => {
    if (!remindingId) return
    const values = await reminderForm.validateFields()
    await reminderMutation.mutateAsync({ id: remindingId, data: values })
    setReminderModalOpen(false)
    setRemindingId(null)
  }

  const handleCreateWorkOrder = async (id: number) => {
    const result = await createWOMutation.mutateAsync(id)
    navigate(`/work-orders?edit=${result.work_order_id}`)
  }

  const columns: ColumnsType<MaintenanceSchedule> = [
    {
      title: 'Veicolo',
      key: 'veicolo',
      render: (_, s) => (
        <Space direction="vertical" size={0}>
          <strong>{s.vehicle_targa ?? '—'}</strong>
          <span style={{ fontSize: 12, color: '#666' }}>{s.vehicle_marca} {s.vehicle_modello}</span>
        </Space>
      ),
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, s) => s.customer_nome
        ? `${s.customer_nome} ${s.customer_cognome ?? ''}`
        : '—',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      render: (v) => v === 'ordinaria' ? <Tag color="cyan">Ordinaria</Tag> : <Tag color="purple">Straordinaria</Tag>,
    },
    {
      title: 'Descrizione',
      dataIndex: 'descrizione',
    },
    {
      title: 'Scadenza',
      key: 'scadenza',
      render: (_, s) => (
        <Space direction="vertical" size={0}>
          {s.data_scadenza && <span>{dayjs(s.data_scadenza).format('DD/MM/YYYY')}</span>}
          {s.km_scadenza && <span style={{ fontSize: 12, color: '#666' }}>{s.km_scadenza.toLocaleString()} km</span>}
          {s.giorni_rimanenti !== null && s.stato === 'attiva' && (
            <span style={{ fontSize: 11, color: s.giorni_rimanenti < 0 ? '#ff4d4f' : '#fa8c16' }}>
              {s.giorni_rimanenti < 0
                ? `Scaduta da ${Math.abs(s.giorni_rimanenti)} gg`
                : `${s.giorni_rimanenti} gg rimanenti`}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Stato',
      key: 'stato',
      render: (_, s) => statoTag(s),
    },
    {
      title: 'Ricorrente',
      dataIndex: 'ricorrente',
      render: (v, s) => v ? (
        <Tooltip title={`ogni ${s.intervallo_giorni ?? '?'} gg / ${s.intervallo_km ?? '?'} km`}>
          <Tag color="geekblue">Sì</Tag>
        </Tooltip>
      ) : <Tag>No</Tag>,
    },
    {
      title: 'Azioni',
      key: 'azioni',
      render: (_, s) => (
        <Space wrap>
          {s.stato === 'attiva' && (
            <>
              <Tooltip title="Crea Accettazione bozza">
                <Button
                  size="small" type="primary" icon={<ThunderboltOutlined />}
                  loading={createWOMutation.isPending}
                  onClick={() => handleCreateWorkOrder(s.id)}
                >
                  Accettazione
                </Button>
              </Tooltip>
              <Tooltip title="Invia reminder al cliente">
                <Button size="small" icon={<BellOutlined />} onClick={() => openReminder(s.id)}>
                  Reminder
                </Button>
              </Tooltip>
              <Tooltip title="Segna completata">
                <Button size="small" icon={<CheckCircleOutlined />} onClick={() => openComplete(s.id)}>
                  Completa
                </Button>
              </Tooltip>
            </>
          )}
          {canCreate && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(s)} />
          )}
          {canDelete && (
            <Popconfirm
              title="Eliminare questa pianificazione?"
              onConfirm={() => deleteMutation.mutate(s.id)}
              okText="Sì" cancelText="No"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const alertCount = schedules.filter(
    s => s.stato === 'attiva' && (s.stato_scadenza === 'scaduta' || s.stato_scadenza === 'prossima')
  ).length

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={2} style={{ margin: 0 }}>Manutenzioni Programmate</Title></Col>
        <Col>
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Nuova Pianificazione
            </Button>
          )}
        </Col>
      </Row>

      {/* Statistiche */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Totali" value={stats.total_schedules} />
            </Card>
          </Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Scadute" value={stats.overdue} valueStyle={{ color: '#cf1322' }} />
            </Card>
          </Col>
          <Col xs={8}>
            <Card size="small">
              <Statistic title="Prossimi 30 gg" value={stats.upcoming_30_days} valueStyle={{ color: '#d46b08' }} />
            </Card>
          </Col>
        </Row>
      )}

      {alertCount > 0 && (
        <Alert
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          message={`${alertCount} pianificazione/i in scadenza o già scaduta/e`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filtri */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Filtra per stato"
          allowClear
          style={{ width: 180 }}
          onChange={(v) => setStatusFilter(v as MaintenanceStatus | undefined)}
        >
          <Option value="attiva">Attive</Option>
          <Option value="completata">Completate</Option>
          <Option value="annullata">Annullate</Option>
        </Select>
        <Checkbox
          checked={overdueOnly}
          onChange={e => setOverdueOnly(e.target.checked)}
        >
          Solo scadute
        </Checkbox>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={schedules}
        loading={isLoading}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (_: number, size: number) => setPageSize(size),
          showTotal: (total: number) => `Totale ${total} manutenzioni`,
        }}
        rowClassName={(s) =>
          s.stato === 'attiva' && s.stato_scadenza === 'scaduta' ? 'maintenance-row-overdue' : ''
        }
      />

      {/* Modal crea/modifica pianificazione */}
      <Modal
        title={editingSchedule ? 'Modifica pianificazione' : 'Nuova pianificazione'}
        open={scheduleModalOpen}
        onOk={handleScheduleSave}
        onCancel={() => setScheduleModalOpen(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width="min(600px, 95vw)"
        okText="Salva"
        cancelText="Annulla"
      >
        <Form form={scheduleForm} layout="vertical" style={{ marginTop: 16 }}>
          {!editingSchedule && (
            <Form.Item name="vehicle_id" label="Veicolo" rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder="Seleziona veicolo"
                filterOption={(input, opt) =>
                  String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={vehiclesData?.items?.map(v => ({
                  value: v.id,
                  label: `${v.targa} — ${v.marca} ${v.modello}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
            <Select>
              <Option value="ordinaria">Ordinaria</Option>
              <Option value="straordinaria">Straordinaria</Option>
            </Select>
          </Form.Item>
          <Form.Item name="descrizione" label="Descrizione" rules={[{ required: true }]}>
            <Input placeholder="es. Tagliando, Cambio olio, Revisione..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="data_scadenza" label="Scadenza data">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="km_scadenza" label="Scadenza km">
                <InputNumber style={{ width: '100%' }} min={0} placeholder="es. 50000" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="giorni_preavviso" label="Preavviso (giorni)" initialValue={30}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="km_preavviso" label="Preavviso (km)" initialValue={1000}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ricorrente" valuePropName="checked" initialValue={false}>
            <Checkbox>Ricorrente (si ripete automaticamente dopo il completamento)</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.ricorrente !== curr.ricorrente}>
            {({ getFieldValue }) => getFieldValue('ricorrente') && (
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="intervallo_giorni" label="Ripeti ogni (giorni)">
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="intervallo_km" label="Ripeti ogni (km)">
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal completamento */}
      <Modal
        title="Segna manutenzione completata"
        open={completeModalOpen}
        onOk={handleComplete}
        onCancel={() => setCompleteModalOpen(false)}
        confirmLoading={completeMutation.isPending}
        okText="Conferma"
        cancelText="Annulla"
      >
        <Form form={completeForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="data_completamento" label="Data completamento">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} defaultValue={dayjs()} />
          </Form.Item>
          <Form.Item name="km_completamento" label="Km al completamento">
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Kilometraggio attuale" />
          </Form.Item>
          <Form.Item name="note_completamento" label="Note">
            <VoiceTextarea rows={3} minHeight={90} maxHeight={200} label="Note" compact />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal reminder */}
      <Modal
        title="Invia reminder al cliente"
        open={reminderModalOpen}
        onOk={handleReminder}
        onCancel={() => setReminderModalOpen(false)}
        confirmLoading={reminderMutation.isPending}
        okText="Invia"
        cancelText="Annulla"
      >
        <Form form={reminderForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="tipo" label="Canale" rules={[{ required: true }]} initialValue="email">
            <Select>
              <Option value="email">Email</Option>
              <Option value="sms">SMS</Option>
              <Option value="whatsapp">WhatsApp</Option>
            </Select>
          </Form.Item>
          <Form.Item name="messaggio_personalizzato" label="Messaggio personalizzato (opzionale)">
            <Input.TextArea
              rows={5}
              placeholder="Lascia vuoto per usare il messaggio standard"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default MaintenanceSchedulesPage
